import React, { useEffect, useState } from 'react';
import DefaultLayout from 'components/DefaultLayout';
import { useIntl } from 'react-intl';
import { ButtonList, StyledForm, StyledPage } from 'styles/layout/form';
import { Button, Input, Switch, TextArea } from 'ui';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Form, Row } from 'antd';
import { pages } from 'constants/pages';
import useQueryApiClient from 'utils/useQueryApiClient';
import toastMessage from 'utils/toastMessage';
import { MapLayerType } from '../index';
import PortalSectionSelect from 'components/Selects/PortalSectionSelect';

const MapLayersCreateEditPage = () => {
  const [isLinkValid, setIsLinkValid] = useState<boolean>(false);

  const intl = useIntl();
  const [form] = Form.useForm();
  const link = Form.useWatch('url', form);
  const navigate = useNavigate();
  const { id } = useParams();
  const {} = useQueryApiClient<MapLayerType>({
    request: {
      url: `api/v1/map-layers/${id}`,
      disableOnMount: !id,
    },
    onSuccess: (response) => {
      form.setFieldsValue(response);
      form.setFields([{ name: 'url', touched: false }]);
      setIsLinkValid(true);
    },
    onError: () => navigate(pages.mapLayers.create.url),
  });
  const { appendData } = useQueryApiClient({
    request: {
      url: `api/v1/map-layers/${id || ''}`,
      method: id ? 'PATCH' : 'POST',
    },
    onSuccess: () => navigate(pages.mapLayers.url),
  });
  const { appendData: validateLink, isLoading: isValidating } = useQueryApiClient({
    request: {
      url: `api/v1/geoproducts/check-service-link`,
      method: 'POST',
    },
    onSuccess: () => {
      toastMessage.success(intl.formatMessage({ id: 'message.geoproduct_service_success' }));
      setIsLinkValid(true);
    },
    onError: () => markLinkInvalid(),
  });

  const isLinkTouched = form.isFieldTouched('url');

  useEffect(() => setIsLinkValid(!isLinkTouched), [link]);

  const onCheckClick = () => {
    const url = form.getFieldValue('url');

    if (url) {
      validateLink({ serviceLink: url, serviceTypeClassifierValueId: 'WMS' });

      return;
    }

    markLinkInvalid();
  };

  const markLinkInvalid = () => {
    form.setFields([{ name: 'url', errors: [intl.formatMessage({id: 'map_layers.url_verification_error'})] }])
    setIsLinkValid(false);
  }

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent>
        <StyledPage>
          <DefaultLayout.PageHeader
            title={intl.formatMessage({ id: id ? pages.mapLayers.edit.title : pages.mapLayers.create.title })}
            breadcrumb={[
              {
                path: '/',
                name: intl.formatMessage({ id: 'general.administration' }),
              },
              {
                path: pages.mapLayers.url,
                name: intl.formatMessage({ id: pages.mapLayers.title }),
              },
            ]}
          />
          <StyledForm>
            <Form form={form} layout="vertical" onFinish={appendData}>
              <Row gutter={20}>
                <Col span={24}>
                  <Switch name="isPublic" label={intl.formatMessage({ id: 'general.public' })} />
                </Col>
                <Col span={12}>
                  <Input name="name" label={intl.formatMessage({ id: 'general.name' })} validations="required" />
                </Col>
                <Col span={12}>
                  <PortalSectionSelect
                    name="type"
                    label={intl.formatMessage({ id: 'map_layers.field_type_label' })}
                    validations="required"
                  />
                </Col>
              </Row>
              <Row gutter={[20, 5]}>
                <Col span={12}>
                  <Input
                    name="url"
                    className="form-item-and-button-one-row"
                    validations="required"
                    label={intl.formatMessage({ id: 'map_layers.field_url_label' })}
                    disabled={isValidating}
                  />
                  <Button
                    className="mt-3"
                    type={isLinkValid ? 'default' : 'primary'}
                    label={intl.formatMessage({ id: 'general.check_link' })}
                    onClick={() => onCheckClick()}
                    loading={isValidating}
                    disabled={isLinkValid && !isLinkTouched}
                  />
                </Col>
                <Col span={12}>
                  <TextArea name="description" label={intl.formatMessage({ id: 'general.description' })} />
                </Col>
              </Row>
              <ButtonList>
                <Button
                  htmlType="submit"
                  type="primary"
                  label={intl.formatMessage({ id: 'general.save' })}
                  disabled={!isLinkValid}
                />
                <Button label={intl.formatMessage({ id: 'general.cancel' })} href={pages.mapLayers.url} />
              </ButtonList>
            </Form>
          </StyledForm>
        </StyledPage>
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default MapLayersCreateEditPage;
